interface SparklineProps {
  data: number[];
  color?: string;
  width?: number;
  height?: number;
  className?: string;
}

/**
 * Minimal inline SVG sparkline — area + line. Hand-rolled (not Recharts) to stay
 * cheap for the dozens of tiny charts in KPI tiles and leaderboard rows.
 */
export function Sparkline({
  data,
  color = "#2563eb",
  width = 96,
  height = 28,
  className = "",
}: SparklineProps) {
  if (data.length === 0) {
    return <svg width={width} height={height} className={className} aria-hidden="true" />;
  }

  const max = Math.max(...data, 1);
  const n = data.length;
  const stepX = n > 1 ? width / (n - 1) : 0;
  const y = (v: number) => height - 2 - (v / max) * (height - 4);

  const points = data.map((v, i) => `${(i * stepX).toFixed(1)},${y(v).toFixed(1)}`);
  const line = points.map((p, i) => `${i === 0 ? "M" : "L"}${p}`).join(" ");
  const area = `${line} L${(width).toFixed(1)},${height} L0,${height} Z`;
  const gradId = `spark-${color.replace("#", "")}`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.22} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradId})`} />
      <path d={line} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
      {n === 1 ? <circle cx={0} cy={y(data[0])} r={2} fill={color} /> : null}
    </svg>
  );
}
